/**
 * FormField - Base form field wrapper component
 *
 * Provides consistent error handling, labeling, and styling for form fields.
 * Integrates with React Hook Form context.
 */

import { useFormContext, get } from "react-hook-form";

export interface FormFieldProps {
  /** Field name (should match form schema key) */
  name: string;

  /** Field label */
  label?: string;

  /** Whether field is required */
  required?: boolean;

  /** Optional hint text to display below the field */
  hint?: string;

  /** Custom validation state (overrides form context) */
  validationState?: "error" | "warning" | "success" | "none";

  /** Custom validation message (overrides form context) */
  validationMessage?: string;

  /** Children (typically the input component) */
  children: React.ReactNode;

  /** Additional CSS class name */
  className?: string;

  /**
   * DOM id of the control this field wraps. When supplied, the label is
   * associated with the control via htmlFor, and the error and hint get stable
   * ids the control can point at with aria-describedby. Optional so existing
   * callers that do not thread an id keep working unchanged.
   */
  fieldId?: string;
}

/**
 * FormField component
 *
 * Wraps form inputs with consistent labeling and error display.
 * Automatically retrieves errors from React Hook Form context.
 *
 * @example
 * ```tsx
 * <FormField name="email" label="Email Address" required>
 *   <Input type="email" />
 * </FormField>
 * ```
 */
export const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  required = false,
  hint,
  validationState: customValidationState,
  validationMessage: customValidationMessage,
  children,
  className,
  fieldId,
}) => {
  const formContext = useFormContext();

  // Get error from form context if available
  const error = formContext ? get(formContext.formState.errors, name) : null;
  const errorMessage = error?.message as string | undefined;

  // Determine validation state
  const validationState = customValidationState || (errorMessage ? "error" : "none");
  const validationMessage = customValidationMessage || errorMessage;

  return (
    <div className={`mb-3 ${className || ""}`}>
      {label && (
        <label
          className="block text-sm font-medium mb-1"
          htmlFor={fieldId}
        >
          {label}
          {required && <span className="text-red-600 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {validationState === "error" && validationMessage && (
        <span
          id={fieldId ? `${fieldId}-error` : undefined}
          role="alert"
          className="text-red-600 text-sm mt-0.5 block"
        >
          {validationMessage}
        </span>
      )}
      {hint && (
        <span
          id={fieldId ? `${fieldId}-hint` : undefined}
          className="text-neutral-500 text-sm mt-0.5 block"
        >
          {hint}
        </span>
      )}
    </div>
  );
};
